/**
 * Kanban Board Component
 * 
 * Main board view that displays tasks organized in columns by status.
 * Supports drag-and-drop functionality for moving tasks between columns.
 * Inspired by Focalboard's Kanban view with Firebase integration.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { ProjectBoard, TaskCard, BoardColumn } from '../../types';
import { getTasks, createTask, updateTask, getDefaultColumns } from '../../services/projectService';
import KanbanColumn from './KanbanColumn';
import TaskDetailModal from './TaskDetailModal';
import './KanbanBoard.css';

interface KanbanBoardProps {
  board: ProjectBoard;
  userEmail: string;
  adminId: string; // Admin document ID for subcollection paths
  onBoardUpdate?: (board: ProjectBoard) => void;
}

/**
 * Main Kanban Board component
 * Displays task columns and handles task creation, updates, and drag-drop
 */
const KanbanBoard: React.FC<KanbanBoardProps> = ({ board, userEmail, adminId, onBoardUpdate }) => {
  const [tasks, setTasks] = useState<TaskCard[]>([]);
  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskCard | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [draggedTask, setDraggedTask] = useState<TaskCard | null>(null);

  /**
   * Initialize columns from board configuration
   */
  useEffect(() => {
    // Get columns from board properties or use defaults
    const statusProperty = board.properties.find(p => p.id === 'status');

    if (statusProperty?.options) {
      const boardColumns: BoardColumn[] = statusProperty.options.map((opt, index) => ({
        id: opt.id,
        title: opt.value,
        color: opt.color,
        order: index,
      }));
      setColumns(boardColumns);
    } else {
      setColumns(getDefaultColumns());
    }
  }, [board.properties]);

  /**
   * Fetch all tasks for this board
   */
  const fetchTasks = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchedTasks = await getTasks(adminId, board.id);
      setTasks(fetchedTasks);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [adminId, board.id]);

  /**
   * Initial load of tasks
   */
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  /**
   * Get tasks for a specific column/status
   */
  const getTasksForColumn = useCallback((columnId: string): TaskCard[] => {
    return tasks
      .filter(task => task.status === columnId)
      .sort((a, b) => a.order - b.order);
  }, [tasks]);

  /**
   * Handle creating a new task in a column
   */
  const handleCreateTask = async (columnId: string) => {
    try {
      const newTask = await createTask(
        adminId,              // Admin ID first
        board.id,
        `New Task`,
        userEmail,
        columnId,
        {
          description: 'Add description...',
        }
      );

      setTasks(prev => [...prev, newTask]);

      // Open the task detail modal for immediate editing
      setSelectedTask(newTask);
      setShowTaskDetail(true);
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task. Please try again.');
    }
  };

  /**
   * Handle clicking on a task card to view details
   */
  const handleTaskClick = async (task: TaskCard) => {
    setSelectedTask(task);
    setShowTaskDetail(true);

    // Refresh tasks to get latest data (including imageUrl)
    try {
      await fetchTasks();
    } catch (err) {
      console.error('Error refreshing tasks:', err);
    }
  };

  /**
   * Handle task update from the detail modal
   */
  const handleTaskUpdate = (updatedTask: TaskCard) => {
    setTasks(prev =>
      prev.map(task => (task.id === updatedTask.id ? updatedTask : task))
    );
  };

  /**
   * Handle task deletion
   */
  const handleTaskDelete = (taskId: string) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
    setShowTaskDetail(false);
    setSelectedTask(null);
  };

  /**
   * Handle drag start event
   */
  const handleDragStart = (task: TaskCard) => {
    setDraggedTask(task);
  };

  /**
   * Handle drag end event
   */
  const handleDragEnd = () => {
    setDraggedTask(null);
  };

  /**
   * Handle drop event on a column
   * Updates task status and reorders tasks in Firebase
   */
  const handleDrop = async (columnId: string, position: number) => {
    if (!draggedTask) return;

    try {

      // Update the task locally immediately for better UX
      const updatedTask = {
        ...draggedTask,
        status: columnId,
        order: position,
      };

      setTasks(prev => {
        const filtered = prev.filter(t => t.id !== draggedTask.id);
        const inColumn = filtered.filter(t => t.status === columnId);

        // Reorder tasks in the column
        const reordered = inColumn.map((task, index) => ({
          ...task,
          order: index >= position ? index + 1 : index,
        }));

        return [...filtered.filter(t => t.status !== columnId), updatedTask, ...reordered];
      });

      // Update in Firebase immediately
      await updateTask(adminId, draggedTask.boardId, draggedTask.id, {
        status: columnId,
        order: position,
      });

      // Notify parent of task update
      handleTaskUpdate(updatedTask);

    } catch (err) {
      console.error('Error moving task:', err);
      setError('Failed to move task. Please try again.');
      // Revert the change by refetching
      await fetchTasks();
    } finally {
      setDraggedTask(null);
    }
  };

  /**
   * Close task detail modal
   */
  const handleCloseTaskDetail = () => {
    setShowTaskDetail(false);
    setSelectedTask(null);
  };

  return (
    <div className="kanban-board">
      {/* Board Header */}
      <div className="kanban-board-header">
        <div className="board-title-section">
          <span className="board-icon">{board.icon}</span>
          <h2 className="board-title">{board.title}</h2>
        </div>
        {board.description && (
          <p className="board-description">{board.description}</p>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="kanban-loading">
          <Spinner animation="border" variant="primary" />
          <p>Loading board...</p>
        </div>
      ) : (
        <>
          {/* Kanban Columns */}
          <div className="kanban-columns-container">
            <div className="kanban-columns">
              {columns
                .sort((a, b) => a.order - b.order)
                .map(column => (
                  <KanbanColumn
                    key={column.id}
                    column={column}
                    tasks={getTasksForColumn(column.id)}
                    onTaskClick={handleTaskClick}
                    onCreateTask={() => handleCreateTask(column.id)}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                    isDragging={draggedTask !== null}
                  />
                ))}
            </div>
          </div>

          {/* Task Detail Modal */}
          {selectedTask && (
            <TaskDetailModal
              show={showTaskDetail}
              task={selectedTask}
              board={board}
              userEmail={userEmail}
              adminId={adminId}
              onClose={handleCloseTaskDetail}
              onUpdate={handleTaskUpdate}
              onDelete={handleTaskDelete}
            />
          )}
        </>
      )}
    </div>
  );
};

export default KanbanBoard;

